import { useCallback, useContext, useEffect, useState } from 'react';
import { match as RouterMatch, useLocation, useRouteMatch } from 'react-router';

import { MDText } from 'i18n-react';

import { ConfigContext } from 'Context/config';

import { stripStringAndCamelCase, titleCaseFirstLetter } from 'Utilities';
import { usePrevious } from 'Utilities/hooks';
import metatitle from 'Utilities/metatitle';
import { REFACTORED_LANDING_PAGE_ROUTES, SELL_MY_CAR_PATH, SELL_YOUR_CAR_PATH } from 'Utilities/wordpress';
import { fetchLandingPage } from 'Utilities/wordpressPages';

import { timeoutFetchSentryGuard } from 'Services/fetch';

import texts from '../../../../texts.json';
import { mileageRegex } from '../../homepage/Components/Hero/Hero.constants';
import { filterComponents, relatedGuidesNames } from '../Pages/LandingPage.helpers';

const MetaTagT = new MDText(texts);

const metaT = (key: string, options?: Record<string, string>) => MetaTagT.translate(key, options) as string;

type MatchParams = {
  vehicle: string;
};

export const useLandingPageData = () => {
  const {
    configActions: { update },
    configState: { landingPageData },
  } = useContext(ConfigContext);
  const { pathname } = useLocation();
  const prevPathname = usePrevious(pathname);
  const match = useRouteMatch<MatchParams>(pathname) ?? {};

  const { params, path } = match as RouterMatch<MatchParams>;

  const isV2WPPage = REFACTORED_LANDING_PAGE_ROUTES.includes(path?.replace(mileageRegex, ''));

  const wpData = landingPageData?.content;
  const [acf, setAcf] = useState(wpData);
  const [loading, setLoading] = useState(!wpData);
  const [stickyVRM, setStickyVRM] = useState(false);

  const updateMetaTitleTags = useCallback(async (
    landingPageContent: { [key: string]: string } | null | undefined,
  ) => {
    const sectionName = stripStringAndCamelCase(path);
    const { vehicle } = params ?? {};

    const title = titleCaseFirstLetter(landingPageContent?.seo_title ?? landingPageContent?.header_title ?? metaT(`landing${sectionName}View.title`, { vehicle }));
    const description = titleCaseFirstLetter(landingPageContent?.seo_description ?? landingPageContent?.header_description ?? metaT(`landing${sectionName}View.description`, { vehicle }));

    metatitle.formatTitle(path, title);
    metatitle.formatDescription(description);
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

  const getLandingPageUrl = useCallback(() => {
    const isMileagePageWithNoContent = !acf && pathname === `${SELL_MY_CAR_PATH}/mileage`;
    const landingPageUrl = isMileagePageWithNoContent ? pathname.replace(mileageRegex, '') : pathname;

    return landingPageUrl
      .split(`${SELL_MY_CAR_PATH}/`).join('')
      .replace(mileageRegex, '')
      .replace(`${SELL_YOUR_CAR_PATH}/`, 'sell-your-car-');
  }, [acf, pathname]);

  const getAcfContent = useCallback(async (landingPageUrl: string) => {
    let apiData;
    setLoading(true);

    try {
      const { content: apiLandingPageData } = await fetchLandingPage(landingPageUrl, isV2WPPage);
      setAcf(apiLandingPageData);
      apiData = apiLandingPageData;
    } catch (error) {
      timeoutFetchSentryGuard(error, () => {
        window?.Sentry?.captureException?.(new Error((error instanceof Error) ? error.message : 'failed to fetch landing page content'), {
          extra: {
            isV2WPPage,
            path,
          },
          level: 'warning',
          tags: {
            fetch: 'Landing Page',
          },
        });
      });
    }
    setLoading(false);
    setStickyVRM(true);
    return apiData;
  }, [isV2WPPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const getRelatedGuidesContent = useCallback(() => (
    filterComponents({ acf, components: relatedGuidesNames })
      ?.map(({ description, thumbnail, title, url }) => ({
        description: acf[description],
        thumbnail: acf[thumbnail],
        title: acf[title],
        url: acf[url] || '',
      }))
  ), [acf]);

  useEffect(() => {
    setStickyVRM(true);

    const updateAcfContentAndMetaDetails = async () => {
      const landingPageUrl = getLandingPageUrl();
      const landingPageContent = await getAcfContent(landingPageUrl);
      updateMetaTitleTags(landingPageContent);
    };

    const isLandingPageRoute = prevPathname !== pathname
      && !pathname.endsWith('/mileage')
      && prevPathname !== undefined;

    if (isLandingPageRoute || !acf) {
      updateAcfContentAndMetaDetails();
    }
  }, [getAcfContent, updateMetaTitleTags]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => (
    () => {
      update({ landingPageData: undefined });
    }
  ), [update]);

  return {
    acf,
    getRelatedGuidesContent,
    loading,
    stickyVRM,
  };
};
